import { addDays, calendarPrefs, rememberCalendar, weekStart } from '../../lib/calendar.js'
import { fromWeek, reorder, todoCounts, weekTitle } from '../../lib/planner.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- calendar-todos ---
let __fez_component_0;
{

__fez_component_0 = class {
  PROPS = {
    day: String,
  }

  init() {
    this.state.loading = true
    this.state.items = []
    this.state.week = ''
    this.state.thisWeek = ''
    this.state.open = calendarPrefs().todos !== false
    this.state.editing = ''
    this.state.adding = false
    this.state.drag = null
    this.state.seq = 0
  }

  onMount() {
    this.on(window, 'focus', () => this.load(), { throttle: 2000 })
  }

  onRefresh(props, changed) {
    if ('day' in changed && weekStart(props.day) !== this.state.week) this.load()
  }

  async load() {
    const seq = ++this.state.seq
    try {
      const data = await window.App.api('planner/todos', { week: this.props.day }, { silent: true })
      if (seq !== this.state.seq) return
      this.state.week = data.week
      this.state.thisWeek = data.this_week
      this.state.items = data.items
    } catch (error) {
      if (seq === this.state.seq) window.Toast.error(error.message)
    } finally {
      if (seq === this.state.seq) this.state.loading = false
    }
  }

  // Every write goes through here: a failure says why and reloads the list as the server has it.
  async write(path, params) {
    try {
      return await window.App.api(path, params, { silent: true })
    } catch (error) {
      window.Toast.error(error.message)
      this.load()
      return null
    }
  }

  replace(todo) {
    this.state.items = this.state.items.map((item) => (item.ref === todo.ref ? todo : item))
  }

  toggle() {
    this.state.open = !this.state.open
    rememberCalendar({ todos: this.state.open })
  }

  async check(ref) {
    const item = this.state.items.find((entry) => entry.ref === ref)
    if (!item) return
    this.replace({ ...item, done: !item.done })
    const saved = await this.write('planner/todo_update', { todo: ref, done: !item.done })
    if (saved) this.replace(saved)
  }

  async addKey(event) {
    if (event.key !== 'Enter' || event.isComposing) return
    event.preventDefault()
    const input = event.target
    const text = input.value.trim()
    if (!text || this.state.adding) return
    this.state.adding = true
    const added = await this.write('planner/todo_add', { week: this.state.week, text })
    this.state.adding = false
    if (!added) return
    input.value = ''
    this.state.items = [...this.state.items, added]
    await this.tick()
    this.find('.td-new')?.focus()
  }

  async edit(ref) {
    this.state.editing = ref
    await this.tick()
    const input = this.find('.td-edit')
    if (!input) return
    input.focus()
    input.setSelectionRange(input.value.length, input.value.length)
  }

  editKey(event, ref) {
    if (event.isComposing) return
    if (event.key === 'Enter') {
      event.preventDefault()
      this.save(ref, event.target.value)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      this.state.editing = ''
    }
  }

  // Leaving the field saves, unless the focus moved to the to-do's own buttons.
  editBlur(event, ref) {
    const row = event.target.closest('.td-item')
    if (event.relatedTarget && row?.contains(event.relatedTarget)) return
    if (this.state.editing === ref) this.save(ref, event.target.value)
  }

  async save(ref, value) {
    const item = this.state.items.find((entry) => entry.ref === ref)
    this.state.editing = ''
    const text = value.trim()
    if (!item || !text || text === item.text) return
    this.replace({ ...item, text })
    const saved = await this.write('planner/todo_update', { todo: ref, text })
    if (saved) this.replace(saved)
  }

  async nextWeek(ref) {
    this.state.editing = ''
    const moved = await this.write('planner/todo_update', { todo: ref, week: addDays(this.state.week, 7) })
    if (!moved) return
    this.state.items = this.state.items.filter((item) => item.ref !== ref)
    window.Toast.success('Moved to next week')
  }

  async remove(ref) {
    this.state.editing = ''
    const done = await this.write('planner/todo_delete', { todo: ref })
    if (done === null) return
    this.state.items = this.state.items.filter((item) => item.ref !== ref)
    window.Toast.success('To-do deleted')
  }

  async place(from, to) {
    const result = reorder(this.state.items, from, to)
    if (!result) return
    const ref = this.state.items[from].ref
    this.state.items = result.items
    await this.write('planner/todo_move', result.before ? { todo: ref, before: result.before } : { todo: ref })
  }

  // Up and Down on a focused handle move the to-do one place and keep the focus on it.
  async nudge(event, index) {
    const step = { ArrowUp: -1, ArrowDown: 1 }[event.key]
    if (!step) return
    event.preventDefault()
    const to = step < 0 ? index - 1 : index + 2
    if (to < 0 || to > this.state.items.length) return
    const ref = this.state.items[index].ref
    const moving = this.place(index, to)
    await this.tick()
    this.find(`.td-item[data-ref="${ref}"] .td-handle`)?.focus()
    await moving
  }

  grab(event, index) {
    if (event.button !== 0) return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    const columns = getComputedStyle(this.find('.td-list')).gridTemplateColumns.split(' ').length
    this.state.drag = { index, gap: index, columns }
  }

  // The gap under the pointer: before the to-do it is over, or after it past its middle.
  drift(event) {
    const drag = this.state.drag
    if (!drag) return
    const rows = [...this.findAll('.td-item')]
    const columns = drag.columns
    let gap = drag.gap
    rows.forEach((row, index) => {
      const box = row.getBoundingClientRect()
      if (event.clientY < box.top || event.clientY > box.bottom) return
      if (columns > 1 && (event.clientX < box.left || event.clientX > box.right)) return
      const past = columns > 1 ? event.clientX > box.left + box.width / 2 : event.clientY > box.top + box.height / 2
      gap = index + (past ? 1 : 0)
    })
    const last = rows[rows.length - 1]?.getBoundingClientRect()
    if (last && event.clientY > last.bottom) gap = rows.length
    if (gap !== drag.gap) this.state.drag = { ...drag, gap }
  }

  drop() {
    const drag = this.state.drag
    if (!drag) return
    this.state.drag = null
    this.place(drag.index, drag.gap)
  }

  cancelDrag() {
    this.state.drag = null
  }

  findAll(selector) {
    return this.root.querySelectorAll(selector)
  }

  beforeRender() {
    const week = this.state.week
    const drag = this.state.drag
    const moving = drag && reorder(this.state.items, drag.index, drag.gap)
    this.state.title = week && this.state.thisWeek ? weekTitle(week, this.state.thisWeek) : 'Sometime this week'
    this.state.counts = todoCounts(this.state.items)
    this.state.rows = this.state.items.map((item, index) => ({
      ...item,
      from: item.week < week ? fromWeek(item.week, week) : '',
      dragged: Boolean(drag) && drag.index === index,
      dropBefore: Boolean(moving) && drag.gap === index,
    }))
    this.state.dropEnd = Boolean(moving) && drag.gap === this.state.items.length
    this.state.across = Boolean(drag) && drag.columns > 1
  }

  CSS = `:fez {
display:block;flex:none;border-bottom:1px solid var(--color-border);background:var(--color-bg-raised);.td-head{display:flex;align-items:center;min-height:36px;padding:0 var(--space-3) 0 var(--space-2)}.td-toggle{display:inline-flex;align-items:center;gap:var(--space-2);min-width:0;min-height:32px;padding:0 var(--space-2);border:0;border-radius:var(--radius-sm);background:transparent;color:var(--color-text);font:inherit;text-align:left;cursor:pointer;&:hover{background:var(--color-bg-hover)}&:focus-visible{outline:2px solid var(--color-focus);outline-offset:-2px}}.td-caret{display:inline-flex;flex:none;color:var(--color-text-subtle)}.td-title{overflow:hidden;font-size:var(--text-sm);font-weight:var(--weight-semibold);text-overflow:ellipsis;white-space:nowrap}.td-count{flex:none;color:var(--color-text-muted);font-size:var(--text-xs)}.td-list{display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:2px var(--space-4);max-height:30vh;margin:0;padding:0 var(--space-4) var(--space-2) var(--space-3);overflow-y:auto;list-style:none}.td-item,.td-add{position:relative;display:flex;align-items:center;gap:var(--space-1);min-width:0;min-height:30px;border-radius:var(--radius-sm)}.td-item{&:hover{background:var(--color-bg-hover)}&.dragged{opacity:0.4}}.td-item.drop-before::before,.td-add.drop-end::before{content:"";position:absolute;top:0;left:0;right:0;height:2px;border-radius:var(--radius-full);background:var(--color-accent)}.td-list.across .td-item.drop-before::before,.td-list.across .td-add.drop-end::before{top:3px;bottom:3px;left:-9px;right:auto;width:2px;height:auto}.td-handle{display:grid;flex:none;place-items:center;width:20px;height:26px;padding:0;border:0;border-radius:var(--radius-sm);background:transparent;color:var(--color-text-subtle);cursor:grab;touch-action:none;opacity:0;&:focus-visible{opacity:1;outline:2px solid var(--color-focus);outline-offset:-2px}}.td-item:hover .td-handle,.td-item.dragged .td-handle{opacity:1}@media (hover:none){.td-handle{opacity:1}}.td-check{display:grid;flex:none;place-items:center;width:18px;height:18px;padding:0;border:1.5px solid var(--color-border-strong);border-radius:var(--radius-full);background:var(--color-bg-raised);color:transparent;cursor:pointer;&:hover{border-color:var(--color-accent)}&:focus-visible{outline:2px solid var(--color-focus);outline-offset:1px}}.td-item.done .td-check{border-color:var(--color-accent);background:var(--color-accent);color:var(--color-accent-text)}.td-text{flex:0 1 auto;min-width:0;padding:var(--space-1) var(--space-1);overflow:hidden;border:0;background:transparent;color:var(--color-text);font:inherit;font-size:var(--text-sm);text-align:left;text-overflow:ellipsis;white-space:nowrap;cursor:text}.td-item.done .td-text{color:var(--color-text-muted);text-decoration:line-through}.td-from{flex:none;padding-right:var(--space-2);color:var(--color-warning);font-size:var(--text-2xs);font-weight:var(--weight-medium);white-space:nowrap}.td-edit,.td-new{flex:1;min-width:0;height:28px;padding:0 var(--space-1);border:1px solid transparent;border-radius:var(--radius-sm);background:transparent;color:var(--color-text);font:inherit;font-size:var(--text-sm);&::placeholder{color:var(--color-text-subtle)}&:focus{border-color:var(--color-accent);outline:2px solid var(--color-focus);outline-offset:-1px;background:var(--color-bg-raised)}}.td-plus{display:grid;flex:none;place-items:center;width:18px;margin-left:24px;color:var(--color-text-subtle)}.td-tools{display:inline-flex;flex:none} :mobile {.td-list{grid-template-columns:minmax(0,1fr);padding:0 var(--space-3) var(--space-2) var(--space-2)}}
}`

  HTML = `<section class="td" aria-label={state.title}>
<div class="td-head">
<button type="button" class="td-toggle" aria-expanded={state.open} onclick="fez.toggle()">
<span class="td-caret"><ui-icon name={state.open ? 'caret-down' : 'caret-right'} size="14"></ui-icon></span>
<span class="td-title">{state.title}</span>
{#if state.counts}<span class="td-count">{state.counts}</span>{/if}
</button>
</div>
{#if state.open}
<ol class="td-list" class:across={state.across}>
{#each state.rows as row, index}
<li class="td-item" class:done={row.done} class:dragged={row.dragged} class:drop-before={row.dropBefore} key={row.ref} data-ref={row.ref}>
<button type="button" class="td-handle" title="Drag to reorder" aria-label="Move {row.text}" onpointerdown="fez.grab(event, {index})" onpointermove="fez.drift(event)" onpointerup="fez.drop()" onpointercancel="fez.cancelDrag()" onkeydown="fez.nudge(event, {index})"><ui-icon name="dots-six-vertical" size="14"></ui-icon></button>
<button type="button" class="td-check" role="checkbox" aria-checked={row.done ? 'true' : 'false'} aria-label={row.done ? 'Open again' : 'Check'} onclick="fez.check('{row.ref}')"><ui-icon name="check" size="11"></ui-icon></button>
{#if state.editing === row.ref}
<input class="td-edit" type="text" value={row.text} maxlength="500" aria-label="To-do" onkeydown="fez.editKey(event, '{row.ref}')" onblur="fez.editBlur(event, '{row.ref}')">
<span class="td-tools" onpointerdown="event.preventDefault()">
<ui-button variant="ghost" size="sm" icon="arrow-bend-up-right" title="Move to next week" onclick="fez.nextWeek('{row.ref}')"></ui-button>
<ui-button variant="ghost" size="sm" icon="trash" title="Delete" onclick="fez.remove('{row.ref}')"></ui-button>
</span>
{:else}
<button type="button" class="td-text" title={row.text} onclick="fez.edit('{row.ref}')">{row.text}</button>
{#if row.from}<span class="td-from">{row.from}</span>{/if}
{/if}
</li>
{/each}
<li class="td-add" class:drop-end={state.dropEnd}>
<span class="td-plus"><ui-icon name="plus" size="14"></ui-icon></span>
<input class="td-new" type="text" maxlength="500" placeholder="Add a to-do" aria-label="Add a to-do" disabled={state.adding} onkeydown="fez.addKey(event)">
</li>
</ol>
{/if}
</section>`
};
}

Fez('calendar-todos', __fez_component_0);

export default __fez_component_0;
